<script lang="ts">
	import Icon from "@components/common/Icon.svelte";
	import Input from "@components/form/Input.svelte";
	import Labelled from "@components/form/Labelled.svelte";
	import type { ComponentProps, Snippet } from "svelte";
	import type { ClassValue, HTMLLabelAttributes } from "svelte/elements";
	import check from "@core/assets/icons/check.svg?raw";
	import { tw } from "@core/utils/svelte/tw.js";

	let {
		value = $bindable(),
		group = $bindable(),
		/* eslint-disable prefer-const */
		wrapperClass,
		label,
		children,
		...rest
		/* eslint-enable prefer-const */
	}: ComponentProps<typeof Input> & {
		wrapperClass?: ClassValue;
		children: Snippet;
		label?: HTMLLabelAttributes;
	} = $props();

	const uniqueId = $props.id();
	const id = $derived(`${rest.id ?? rest.name ?? ""}-${uniqueId}`);
	const isRadioOrCheckbox = $derived(["checkbox", "radio"].includes(String(rest.type)));
	const labelClass = $derived(
		tw(
			label?.class,
			"text-cmctext md:text-base/tight font-semibold",
			isRadioOrCheckbox
				? "tracking-[0.02em] cursor-pointer text-2xs/none"
				: "uppercase tracking-[0.05em] text-2xs/tight",
		),
	);
</script>

<Labelled
	{wrapperClass}
	required={rest.required}
	label={{ ...label, class: labelClass, for: id }}
	horizontal={isRadioOrCheckbox}
>
	{@render children()}
	{#snippet content()}
		{#if rest.type === "checkbox"}
			<div class="relative size-5 md:size-8">
				<Input
					{...rest}
					{id}
					bind:group
					{value}
					class={[rest.class, "appearance-none size-5 md:size-8 rounded-[2px] md:rounded border-cmclightgray"]}
				/>
				<div class="absolute size-5 md:size-8 top-0 left-0 self-start [:not(:checked)+&]:hidden pointer-events-none">
					<Icon icon={check} class="size-5 md:size-8 text-cmcprimary" />
				</div>
			</div>
		{:else if rest.type === "radio"}
			<Input {...rest} {id} bind:group {value} />
		{:else}
			<Input {...rest} {id} bind:value />
		{/if}
	{/snippet}
</Labelled>
